/**
 * AuthorBio - rich E-E-A-T author block for blog post footers.
 *
 * Displays:
 *  - Edited portrait with dark amber bokeh / concrete texture background
 *  - Full name, title, and credentials
 *  - Sector keywords (SEO-rich)
 *  - Core capabilities tag cloud
 *  - Links: full profile (/modi-elnadi), LinkedIn, Editorial Policy
 *  - Latest Posts by Modi - 3 most recent articles (dynamic, excludes current post)
 *
 * Structured data (Person schema) is injected by BlogPost.tsx at the page level,
 * so this component is purely presentational.
 */

import { useMemo, useState } from "react";
import { Link } from "wouter";
import { ExternalLink, Linkedin, ArrowRight, BookOpen, Link2, Check } from "lucide-react";
import { trackBlogShareClick, trackCalendlyClick } from "@/lib/analytics";
import { blogPosts } from "@/data/blogPosts";
import { canonicalPath } from "@/lib/canonicalPath";
import { toast } from "sonner";
import { ResponsiveImage } from "@/components/ResponsiveImage";

const AUTHOR_PHOTO = "https://integrated.social/media/modi-elnadi-founder-portrait_7714f45e.jpg";
const AUTHOR_PHOTO_WEBP = "/media/modi-elnadi-ai-growth-director-london-portrait-480_2b4c21b9.webp";
const AUTHOR_PHOTO_ALT = "Illustrated portrait of Modi Elnadi, founder of Integrated.Social in London";

const SECTORS = [
  "B2B SaaS",
  "FinTech",
  "Ecommerce",
  "Sports Media",
  "FMCG",
  "Telecoms",
  "Travel & Tourism",
  "Cybersecurity",
  "Enterprise AI",
];

const CAPABILITIES = [
  "Agentic AI Systems",
  "GTM Strategy",
  "AI Search (SEO/AEO/GEO/LLMO)",
  "PPC & Performance Max",
  "Demand Generation",
  "Account-Based Marketing (ABM)",
  "B2B Marketing",
  "B2B2C Marketing",
  "B2C Marketing",
  "Performance Marketing",
  "Content Strategy",
  "LLMs & Prompt Engineering",
  "CRM & RevOps",
  "Brand Positioning",
  "Persona-Driven Campaigns",
  "A/B Testing & CRO",
];

interface AuthorBioProps {
  /** Slug of the current post - excluded from the Latest Posts list */
  currentSlug?: string;
  /** Title used in copy-link event metadata. */
  articleTitle?: string;
  /** Canonical article URL used for the nearby share control. */
  canonicalUrl?: string;
}

export default function AuthorBio({ currentSlug, articleTitle, canonicalUrl }: AuthorBioProps = {}) {
  const [copied, setCopied] = useState(false);
  const shareUrl = canonicalUrl ?? (currentSlug ? `https://integrated.social/blog/${currentSlug}/` : null);

  // Derive the 3 most recent posts, excluding the current one
  const latestPosts = useMemo(() => {
    return [...blogPosts]
      .filter((p) => p.slug !== currentSlug)
      .sort((a, b) => {
        const dateA = new Date(a.dateModified ?? a.date).getTime();
        const dateB = new Date(b.dateModified ?? b.date).getTime();
        return dateB - dateA;
      })
      .slice(0, 3);
  }, [currentSlug]);

  const handleCopyArticleLink = async () => {
    if (!shareUrl) return;

    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API is unavailable");
      await Promise.race([
        navigator.clipboard.writeText(shareUrl),
        new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error("Clipboard permission timed out")), 750);
        }),
      ]);
    } catch {
      const fallback = document.createElement("textarea");
      fallback.value = shareUrl;
      fallback.setAttribute("readonly", "");
      fallback.style.position = "fixed";
      fallback.style.opacity = "0";
      document.body.appendChild(fallback);
      fallback.select();
      document.execCommand("copy");
      document.body.removeChild(fallback);
    }

    setCopied(true);
    toast.success("Link copied", { description: "Ready to share with your team." });
    trackBlogShareClick({
      platform: "copy_link",
      post_slug: currentSlug ?? "",
      post_title: articleTitle ?? "Integrated.Social article",
      share_method: "icon_button",
    });
    window.setTimeout(() => setCopied(false), 2500);
  };

  return (
    <section
      aria-label="About the author"
      className="mt-14 rounded-xl overflow-hidden border border-border"
      itemScope
      itemType="https://schema.org/Person"
    >
      {/* Hidden machine-readable metadata */}
      <meta itemProp="name" content="Modi Elnadi" />
      <meta itemProp="jobTitle" content="Founder & Director of Marketing and AI Growth" />
      <meta itemProp="url" content="https://integrated.social/modi-elnadi/" />
      <meta itemProp="sameAs" content="https://www.linkedin.com/in/modielnadi/" />

      {/* ── Dark header band ── */}
      <div className="bg-[#0F1419] px-6 py-3 flex items-center gap-2">
        <span className="text-[#FF8C00] text-xs font-mono tracking-widest uppercase">About the Author</span>
      </div>

      {/* ── Main content ── */}
      <div className="bg-[#F7F5F2] p-6 md:p-8">
        <div className="flex flex-col sm:flex-row gap-6 md:gap-8">

          {/* Portrait */}
          <div className="shrink-0 flex flex-col items-center gap-3">
            <div className="w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden ring-2 ring-[#FF8C00]/40 shadow-lg">
              <picture>
                <source type="image/webp" srcSet={AUTHOR_PHOTO_WEBP} />
                <img
                  src={AUTHOR_PHOTO}
                  alt={AUTHOR_PHOTO_ALT}
                  width={1086}
                  height={1448}
                  className="w-full h-full object-cover object-top"
                  itemProp="image"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            {/* Social links under photo on desktop */}
            <div className="hidden sm:flex flex-col items-center gap-2">
              <a
                href="https://www.linkedin.com/in/modielnadi/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[#0077B5] hover:text-[#005E93] font-medium transition-colors"
                aria-label="Modi Elnadi on LinkedIn"
              >
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </a>
              <a
                href="https://x.com/MoElnadi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[#1A1A2E] hover:text-[#FF8C00] font-medium transition-colors"
                aria-label="Modi Elnadi on X / Twitter"
              >
                𝕏 @MoElnadi
              </a>
            </div>
          </div>

          {/* Bio content */}
          <div className="flex-1 min-w-0">
            {/* Name & title */}
            <div className="mb-3">
              <Link
                href="/modi-elnadi/"
                className="group inline-block"
                aria-label="View Modi Elnadi's full profile"
              >
                <h3
                  className="font-serif text-xl md:text-2xl text-[#1A1A2E] group-hover:text-[#FF8C00] transition-colors leading-tight"
                  itemProp="name"
                >
                  Modi Elnadi
                </h3>
              </Link>
              <p className="text-[#FF8C00] text-sm font-semibold mt-0.5" itemProp="jobTitle">
                Founder &amp; Director of Marketing and AI Growth · Integrated.Social
              </p>
              <p className="text-[#6B7280] text-xs mt-1">
                MBA, University of Surrey (Honors) · London, UK · Founded 2014
              </p>
            </div>

            {/* Bio paragraph - SEO-rich, E-E-A-T signals */}
            <p
              className="text-[#4B5563] text-sm leading-relaxed mb-4"
              itemProp="description"
            >
              Modi Elnadi is the founder of{" "}
              <Link href="/about/" className="text-[#FF8C00] hover:underline font-medium">
                Integrated.Social
              </Link>
              , a boutique <strong>B2B, B2B2C, and B2C</strong> growth marketing agency established in London in 2014. With 16+ years
              deploying revenue-generating marketing systems across{" "}
              <strong>B2B SaaS, FinTech, Ecommerce, Sports Media, FMCG, Telecoms, and Travel &amp; Tourism</strong>,
              Modi specializes in{" "}
              <Link href="/services/gemini-agentic-ai/" className="text-[#FF8C00] hover:underline font-medium">
                Agentic AI lead generation
              </Link>
              ,{" "}
              <Link href="/services/seo-aeo-geo/" className="text-[#FF8C00] hover:underline font-medium">
                AI Search Optimization (SEO/AEO/GEO/LLMO)
              </Link>
              , and{" "}
              <Link href="/services/ppc-performance-max/" className="text-[#FF8C00] hover:underline font-medium">
                PPC &amp; Performance Max
              </Link>
              . He has managed $25M+ in paid media, delivered 5x–35x ROAS, and built multi-agent AI
              systems that generate pipeline daily at scale. Every engagement is consultative,
              data-driven, and ROI-accountable.
            </p>

            {/* Sector keywords - SEO-rich tag cloud */}
            <div className="mb-4">
              <p className="text-[#9CA3AF] text-xs font-mono uppercase tracking-widest mb-2">Sectors</p>
              <div className="flex flex-wrap gap-1.5">
                {SECTORS.map((sector) => (
                  <span
                    key={sector}
                    className="text-xs px-2 py-0.5 rounded bg-white border border-[#E8E4DF] text-[#4B5563] font-medium"
                  >
                    {sector}
                  </span>
                ))}
              </div>
            </div>

            {/* Capabilities tag cloud */}
            <div className="mb-5">
              <p className="text-[#9CA3AF] text-xs font-mono uppercase tracking-widest mb-2">Expertise</p>
              <div className="flex flex-wrap gap-1.5">
                {CAPABILITIES.map((cap) => (
                  <span
                    key={cap}
                    className="text-xs px-2 py-0.5 rounded bg-[#FF8C00]/10 border border-[#FF8C00]/20 text-[#CC6600] font-medium"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA links row */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-4 border-t border-[#E8E4DF]">
              <Link
                href="/modi-elnadi/"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF8C00] hover:text-[#E67E00] transition-colors"
              >
                Full profile &amp; case studies <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link
                href="/about/"
                className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#FF8C00] font-medium transition-colors"
              >
                About Integrated.Social
              </Link>
              <a
                href="https://www.linkedin.com/in/modielnadi/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#0077B5] hover:text-[#005E93] font-medium transition-colors"
              >
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </a>
              {shareUrl && (
                <button
                  type="button"
                  onClick={handleCopyArticleLink}
                  aria-label={copied ? "Article link copied" : "Copy article link"}
                  className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-[#E8E4DF] px-3 text-sm font-medium text-[#4B5563] transition-colors hover:border-[#FF8C00]/50 hover:text-[#CC6600] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8C00] focus-visible:ring-offset-2"
                  data-testid="author-bio-copy-link"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" /> : <Link2 className="h-3.5 w-3.5" aria-hidden="true" />}
                  {copied ? "Copied" : "Copy link"}
                </button>
              )}
              <Link
                href="/editorial-policy/"
                className="inline-flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#FF8C00] transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Editorial Policy
              </Link>
              <a
                href="https://calendly.com/modielnadi/30min"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCalendlyClick("author_bio_book_call")}
                className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold bg-[#FF8C00] text-white px-4 py-1.5 rounded-lg hover:bg-[#E67E00] transition-colors"
              >
                Book a call
              </a>
              <span className="sr-only" aria-live="polite">
                {copied ? "Article link copied to clipboard." : ""}
              </span>
            </div>
          </div>
        </div>

        {/* ── Latest Posts by Modi ── */}
        {latestPosts.length > 0 && (
          <div className="mt-8 pt-6 border-t border-[#E8E4DF]">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-4 h-4 text-[#FF8C00]" />
              <p className="text-[#9CA3AF] text-xs font-mono uppercase tracking-widest">
                Latest Posts by Modi
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {latestPosts.map((post) => (
                <Link
                  key={post.slug}
                  href={canonicalPath(`/blog/${post.slug}`)}
                  className="group flex flex-col gap-2 rounded-lg overflow-hidden border border-[#E8E4DF] bg-white hover:border-[#FF8C00]/40 hover:shadow-md transition-all duration-200"
                  aria-label={`Read: ${post.title}`}
                >
                  {/* Thumbnail */}
                  {post.heroImage && (
                    <div className="w-full h-28 overflow-hidden bg-[#0F1419]">
                      <ResponsiveImage
                        src={post.heroImage}
                        alt={post.title}
                        width={640}
                        height={360}
                        sizes="(min-width: 640px) 33vw, calc(100vw - 48px)"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  )}
                  {/* Text */}
                  <div className="px-3 pb-3 flex flex-col gap-1">
                    {/* Tag pill */}
                    {post.tags?.[0] && (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-[#FF8C00] font-semibold">
                        {post.tags[0]}
                      </span>
                    )}
                    <p className="text-[#1A1A2E] text-xs font-semibold leading-snug group-hover:text-[#FF8C00] transition-colors line-clamp-3">
                      {post.title}
                    </p>
                    <p className="text-[#9CA3AF] text-[10px] mt-auto pt-1">
                      {new Date(post.dateModified ?? post.date).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
